import { numericUtil } from '@cmg/common';
import type { CellClassRules, DesignSystemTooltipProps } from '@cmg/data-grid';
import { CellClassNames, createValueFormatter } from '@cmg/data-grid';
import isNil from 'lodash/isNil';

import { RevisionTooltipContent } from '../../../../../../common/components/data-grid/RevisionTooltipContent';
import type { RevisionPairs } from '../../../../../../common/util/revision';
import { isRevised } from '../../../../../../common/util/revision';
import type { CalendarGridColDef, CalendarGridRowData } from '../../CalendarGrid.model';
import { CALENDAR_GRID_CELL_CLASS_NAMES } from '../../CalendarGrid.model';

export type Value = NonNullable<CalendarGridRowData['attributes']>['latestSizeInSecuritiesTotal'];
export type TooltipFiledValue = NonNullable<
  CalendarGridRowData['attributes']
>['initialSizeInSecuritiesTotal'];

export const valueFormatter = createValueFormatter<CalendarGridRowData, Value>({
  gridRow: ({ value }) => numericUtil.getDisplayValueForInteger(value),
});

const getRevisionPairs = (data: CalendarGridRowData | undefined): RevisionPairs => {
  return [
    [data?.attributes?.initialSizeInSecuritiesTotal, data?.attributes?.latestSizeInSecuritiesTotal],
  ];
};

export const getTooltipContent: NonNullable<
  DesignSystemTooltipProps<CalendarGridRowData, TooltipFiledValue>['getContent']
> = ({ data, value }) => {
  if (!isNil(value) && isRevised(getRevisionPairs(data))) {
    return () => (
      <RevisionTooltipContent
        initialValue={`Initial Size: ${numericUtil.getDisplayValueForInteger(value)}`}
      />
    );
  }
};

export const cellClassRules: CellClassRules<CalendarGridRowData, Value> = {
  [CALENDAR_GRID_CELL_CLASS_NAMES.Revised]: ({ data }) => isRevised(getRevisionPairs(data)),
  [CellClassNames.CELL_WITH_TOOLTIP]: ({ data }) => isRevised(getRevisionPairs(data)),
};

const sizeInSecuritiesTotalColDef: CalendarGridColDef<Value> = {
  field: 'attributes.latestSizeInSecuritiesTotal',
  headerName: 'Size in Securities Total',
  width: 124,
  type: ['numericColumn', 'number'],
  filter: 'agNumberColumnFilter',
  valueFormatter,
  cellClassRules,
  tooltipField: 'attributes.initialSizeInSecuritiesTotal',
  tooltipComponentParams: {
    getContent: getTooltipContent,
  },
};

export default sizeInSecuritiesTotalColDef;
